import React from 'react';
import { ArrowRight, ShieldCheck, DollarSign, HelpCircle, AlertTriangle, MapPin, Wrench, FileText, Clock, BadgeCheck, CreditCard, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import GlobalBanner from '@/components/GlobalBanner';
import RelatedArticles from '@/components/RelatedArticles';

const CONTRACTOR_FACTORS = [
  { title: 'Licensing & Insurance', desc: 'Verify that the contractor holds a valid state license and carries both general liability and workers\u2019 compensation insurance. This protects you if anything goes wrong during the installation or repair.', icon: BadgeCheck },
  { title: 'Residential HVAC Experience', desc: 'Look for contractors with a proven track record installing and servicing residential systems in Southeast Louisiana. Local experience means they understand the unique demands of our climate.', icon: Wrench },
  { title: 'Clear Written Estimates', desc: 'A professional contractor should provide a detailed written estimate that clearly explains the scope of work, equipment being installed, and any additional costs.', icon: FileText },
  { title: 'Warranty Coverage', desc: 'Ask about warranty coverage on both the equipment and the labor. A quality contractor will stand behind their work with meaningful warranty protection.', icon: ShieldCheck },
  { title: 'Maintenance & Long-Term Support', desc: 'HVAC systems need regular maintenance to perform their best. Choose a contractor that offers maintenance plans and ongoing service support after installation.', icon: Clock },
  { title: 'Financing Options', desc: 'Major HVAC work can be a significant investment. Many reputable contractors offer financing options to help homeowners manage the cost of repairs or replacements.', icon: CreditCard },
];

const QUESTIONS = [
  'How was the system size determined for my home?',
  'What specific installation work is included in the estimate?',
  'What warranty coverage is provided on equipment and labor?',
  'What maintenance support is available after installation?',
  'Are there any additional costs not included in this estimate?',
  'How long has your company been servicing this area?',
];

const WARNING_SIGNS = [
  { text: 'Extremely low estimates compared to other contractors', desc: 'An unusually low price may indicate corners being cut on materials, installation quality, or permit compliance.' },
  { text: 'High-pressure sales tactics', desc: 'A reputable contractor will give you time to review estimates and make an informed decision without aggressive urgency.' },
  { text: 'No written scope of work', desc: 'Any professional estimate should include a detailed written description of exactly what work will be performed.' },
  { text: 'Lack of licensing or insurance documentation', desc: 'If a contractor cannot provide proof of licensing and insurance when asked, consider it a serious red flag.' },
];

const SERVICE_AREAS = ['New Orleans', 'Metairie', 'Kenner', 'Covington', 'Mandeville', 'Hammond', 'Slidell'];

const SERVICES = [
  'System replacement',
  'System repair',
  'Preventive maintenance',
  'Duct system evaluation',
  'Indoor air quality improvements',
];

const CompareContractorsPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <GlobalBanner />
      <Navbar />

      {/* Hero */}
      <section className="pt-28 pb-12 bg-gradient-to-br from-slate-50 to-white" data-testid="compare-contractors-hero">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h1
            className="text-4xl md:text-5xl font-bold text-[#0F172A] mb-4"
            style={{ fontFamily: 'Outfit, sans-serif' }}
            data-testid="compare-contractors-heading"
          >
            How to Compare HVAC Contractors in Southeast Louisiana
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto mb-4">
            Choosing the right HVAC contractor is one of the most important decisions a homeowner
            can make. The quality of your installation directly affects system performance,
            energy efficiency, reliability, and long-term maintenance costs.
          </p>
          <p className="text-base text-slate-500 max-w-2xl mx-auto mb-8">
            This guide helps homeowners understand what to look for — and what to watch out for —
            when comparing HVAC contractors in the Greater New Orleans and Northshore region.
          </p>
          <a href="/instant-hvac-estimate-new-orleans">
            <Button
              data-testid="hero-estimate-btn"
              className="bg-[#F97316] hover:bg-[#EA580C] text-white font-bold py-4 px-8 rounded-full text-lg"
            >
              Get Your Instant HVAC Estimate
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </a>
        </div>
      </section>

      {/* SECTION 1 — What to Look For */}
      <section className="py-16" data-testid="what-to-look-for-section">
        <div className="max-w-4xl mx-auto px-6">
          <div className="flex items-center gap-3 mb-2">
            <ShieldCheck className="w-7 h-7 text-[#0EA5E9]" />
            <h2 className="text-3xl font-bold text-[#0F172A]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              What to Look for When Choosing an HVAC Contractor
            </h2>
          </div>
          <p className="text-slate-600 text-lg mb-8">
            Before signing any agreement, evaluate each contractor across these key areas:
          </p>

          <div className="grid sm:grid-cols-2 gap-4">
            {CONTRACTOR_FACTORS.map((item) => (
              <div key={item.title} data-testid={`factor-card-${item.title.toLowerCase().replace(/\s/g, '-')}`} className="bg-slate-50 p-6 rounded-xl border border-slate-200">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-9 h-9 bg-sky-50 rounded-lg flex items-center justify-center">
                    <item.icon className="w-5 h-5 text-[#0EA5E9]" />
                  </div>
                  <h3 className="font-bold text-[#0F172A] text-base" style={{ fontFamily: 'Outfit, sans-serif' }}>{item.title}</h3>
                </div>
                <p className="text-sm text-slate-500 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 2 — Lowest Price Warning */}
      <section className="py-16 bg-slate-50" data-testid="lowest-price-section">
        <div className="max-w-4xl mx-auto px-6">
          <div className="flex items-center gap-3 mb-2">
            <DollarSign className="w-7 h-7 text-[#F97316]" />
            <h2 className="text-3xl font-bold text-[#0F172A]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Why the Lowest Price Is Not Always the Best Option
            </h2>
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 p-8 mt-6 space-y-4">
            <p className="text-slate-600 text-base leading-relaxed">
              HVAC system performance depends heavily on <strong>proper installation and accurate
              system sizing</strong>. An undersized or improperly installed system will run
              inefficiently, break down more often, and cost more in the long run — regardless of
              how much you saved up front.
            </p>
            <p className="text-slate-600 text-base leading-relaxed">
              A lower price may sometimes indicate an <strong>incomplete installation scope</strong>,
              such as skipping permit work, reusing incompatible components, or not performing a
              proper load calculation. It can also reflect lower quality workmanship that leads to
              premature equipment failure.
            </p>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 mt-4">
              <p className="text-slate-700 text-sm font-medium">
                <strong>Homeowner tip:</strong> When comparing estimates, don't just compare the
                bottom-line price. Review what is included in each estimate — equipment model, labor
                scope, permits, warranty terms, and any exclusions. The best value often comes from
                the contractor who provides the most complete and transparent estimate.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 3 — Questions to Ask */}
      <section className="py-16" data-testid="questions-to-ask-section">
        <div className="max-w-4xl mx-auto px-6">
          <div className="flex items-center gap-3 mb-2">
            <HelpCircle className="w-7 h-7 text-[#0EA5E9]" />
            <h2 className="text-3xl font-bold text-[#0F172A]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Questions Homeowners Should Ask
            </h2>
          </div>
          <p className="text-slate-600 text-lg mb-8">
            Before committing to any contractor, consider asking these questions to better
            understand their approach and professionalism:
          </p>

          <div className="space-y-3">
            {QUESTIONS.map((q, i) => (
              <div
                key={q}
                data-testid={`question-item-${i}`}
                className="flex items-start gap-4 bg-slate-50 p-5 rounded-xl border border-slate-200"
              >
                <div className="w-8 h-8 bg-[#0EA5E9] rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold text-xs">{i + 1}</span>
                </div>
                <p className="text-slate-700 text-base font-medium pt-1">{q}</p>
              </div>
            ))}
          </div>

          <p className="text-slate-500 text-sm mt-6 italic">
            A contractor who answers these questions clearly and confidently is more likely to
            deliver quality work and stand behind the installation.
          </p>
        </div>
      </section>

      {/* SECTION 4 — Warning Signs */}
      <section className="py-16 bg-slate-50" data-testid="warning-signs-section">
        <div className="max-w-4xl mx-auto px-6">
          <div className="flex items-center gap-3 mb-2">
            <AlertTriangle className="w-7 h-7 text-red-500" />
            <h2 className="text-3xl font-bold text-[#0F172A]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Warning Signs to Watch For
            </h2>
          </div>
          <p className="text-slate-600 text-lg mb-8">
            Be cautious if you notice any of these potential red flags during the evaluation process:
          </p>

          <div className="space-y-4">
            {WARNING_SIGNS.map((item) => (
              <div key={item.text} data-testid={`warning-sign-${item.text.slice(0, 15).replace(/\s/g, '-')}`} className="bg-white rounded-xl border border-slate-200 p-5">
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-red-50 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <AlertTriangle className="w-3.5 h-3.5 text-red-500" />
                  </div>
                  <div>
                    <h3 className="font-bold text-[#0F172A] text-base" style={{ fontFamily: 'Outfit, sans-serif' }}>{item.text}</h3>
                    <p className="text-sm text-slate-500 mt-1">{item.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 5 — Local HVAC Professionals */}
      <section className="py-16" data-testid="local-professionals-section">
        <div className="max-w-4xl mx-auto px-6">
          <div className="flex items-center gap-3 mb-2">
            <MapPin className="w-7 h-7 text-[#0EA5E9]" />
            <h2 className="text-3xl font-bold text-[#0F172A]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Local HVAC Professionals
            </h2>
          </div>
          <p className="text-slate-600 text-lg mb-8">
            NOLA HVAC Connect helps homeowners connect with qualified HVAC professionals serving
            communities across Southeast Louisiana.
          </p>

          <div className="grid sm:grid-cols-2 gap-6">
            {/* Service Areas */}
            <div className="bg-slate-50 rounded-2xl border border-slate-200 p-6">
              <h3 className="font-bold text-[#0F172A] text-lg mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Areas We Serve
              </h3>
              <div className="space-y-2">
                {SERVICE_AREAS.map((area) => (
                  <div key={area} className="flex items-center gap-2.5">
                    <MapPin className="w-4 h-4 text-[#0EA5E9] flex-shrink-0" />
                    <span className="text-slate-600 text-sm font-medium">{area}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Services */}
            <div className="bg-slate-50 rounded-2xl border border-slate-200 p-6">
              <h3 className="font-bold text-[#0F172A] text-lg mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Services Typically Provided
              </h3>
              <div className="space-y-2">
                {SERVICES.map((service) => (
                  <div key={service} className="flex items-center gap-2.5">
                    <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0" />
                    <span className="text-slate-600 text-sm font-medium">{service}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#0F172A]" data-testid="compare-contractors-cta">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Ready to Connect with a Qualified HVAC Professional?
          </h2>
          <p className="text-slate-300 text-lg mb-8">
            Request assistance connecting with a qualified HVAC professional in your area.
            NOLA HVAC Connect makes it easy to take the next step.
          </p>
          <a href="/instant-hvac-estimate-new-orleans">
            <Button
              data-testid="cta-request-service-btn"
              className="bg-[#F97316] hover:bg-[#EA580C] text-white font-bold py-4 px-10 rounded-full text-lg orange-glow transition-all duration-300 transform hover:-translate-y-1"
            >
              Request HVAC Service
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </a>
        </div>
      </section>

      {/* Related Articles */}
      <RelatedArticles currentPath="/compare-hvac-contractors" />

      <Footer />
    </div>
  );
};

export default CompareContractorsPage;
